import type {
  ToolApprovalCounts,
  ToolApprovalFilter,
  ToolApprovalTab,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { usePersonalToolApprovalCounts } from '@/hooks/api/useShadowAiToolApprovals';
import {
  useToolApprovalShowFilter,
  type ToolApprovalShowFilterTableProps,
} from './useToolApprovalShowFilter';

export interface UsePersonalToolApprovalTableOptions {
  /** DataTable id — keys the Show filter's URL param. */
  tableId: string;
  tab: ToolApprovalTab;
  /** Poll counts with the table (Running Now polls faster). */
  countsRefetchInterval?: number;
}

/** Props spread onto the tab's `<DataTable>`; empty when the flag is off. */
export type PersonalToolApprovalDataTableProps =
  ToolApprovalShowFilterTableProps;

export interface PersonalToolApprovalTableState {
  /** `shadow_ai_tool_approval` flag (false while flags load). */
  enabled: boolean;
  /** True until the flag resolves — hold the table back meanwhile. */
  isFlagLoading: boolean;
  /** Show filter for the list `approval` param; undefined when flag is off. */
  approvalFilter: ToolApprovalFilter | undefined;
  counts: ToolApprovalCounts | undefined;
  dataTableProps: PersonalToolApprovalDataTableProps;
}

/**
 * KIR-4864: tool approval on a personal Shadow AI tab — flag gate and the Show
 * filter (URL state, personal tab totals, toolbar control, Clear-filters
 * reset). Personal tabs belong to the account owner alone, so there is no
 * role gate; approvals are the owner's personal ones, never an org's.
 */
export function usePersonalToolApprovalTable({
  tableId,
  tab,
  countsRefetchInterval,
}: UsePersonalToolApprovalTableOptions): PersonalToolApprovalTableState {
  const { flags, isLoading: isFlagLoading } = useFeatureFlags([
    flagsmithFeatureFlags.SHADOW_AI_TOOL_APPROVAL,
  ]);
  const enabled = flags[flagsmithFeatureFlags.SHADOW_AI_TOOL_APPROVAL] ?? false;

  const { data: counts } = usePersonalToolApprovalCounts(tab, {
    enabled,
    refetchInterval: countsRefetchInterval,
  });
  const { filter, dataTableProps } = useToolApprovalShowFilter({
    enabled,
    tableId,
    persistInUrl: true,
    counts,
  });

  return {
    enabled,
    isFlagLoading,
    approvalFilter: enabled ? filter : undefined,
    counts,
    dataTableProps,
  };
}
